import { React, useState, useRef, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import moment from 'moment';
import styled from 'styled-components';

import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';
import { useLanguage } from '../../Context/LanguageContext';

import SalesCalendarWeekRow from '../../Model/Individual/StatisticsModel/SalesCalendarWeekRow';
import SalesDaily from '../../Model/Individual/StatisticsModel/SalesDaily';

import IntroMarkdown from './IntroMarkdown';

export default function SalesViewRenderer(props) {
  const { data } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const history = useHistory();

  const [selectedDate, setSelectedDate] = useState(null);

  const salesDailyRef = useRef(null);

  const { intro, mode } = data;
  const isDaily = mode === 'daily';

  const today = useMemo(() => moment(), []);

  // weekly: ISO 문자열 → moment 변환 //
  const momentWeeksData = useMemo(() => {
    if (isDaily) return [];

    return data.weeksData.map(weekData =>
      weekData.map(dayData => ({ ...dayData, date: moment(dayData.date) }))
    );
  }, [data.weeksData, isDaily]);

  // 모달 바깥 클릭 시 닫기 (weekly 모드) //
  useEffect(() => {
    if (isDaily || !selectedDate) return;

    function onClickOutside(e) {
      if (salesDailyRef.current && !salesDailyRef.current.contains(e.target)) {
        setSelectedDate(null);
      }
    }

    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [selectedDate, isDaily]);

  // 매출 항목 클릭 시 회원 상세 이동 //
  function onClickSalesItem(itemData) {
    if (itemData.memberId === undefined) {
      alert(t('statistics.sales.alertNotRegistered'));
      return;
    }

    history.push('/member/valid/detail/' + itemData.memberId);
  }

  // weekly: 날짜 클릭 //
  function onClickDateBox(clickedDate) {
    if (selectedDate && selectedDate.isSame(clickedDate, 'day')) {
      setSelectedDate(null);
      return;
    }

    setSelectedDate(clickedDate.clone());
  }

  return (
    <TopLevelWrapper>
      <IntroMarkdown text={intro} color={theme.text} fontFamily='NotoSansKR-Regular' />
      {
        isDaily &&
        <SalesDaily
          date={moment(data.date)}
          data={data.daySales}
          hideClose
          onClickClose={() => {}}
          onClickSalesItem={onClickSalesItem} />
      }
      {
        !isDaily &&
        momentWeeksData.map((weekData, weekIndex) => (
          <WeekSection key={'sales-week-' + weekIndex}>
            <WeekTitle $color={theme.text}>
              {weekData[0].date.format('MM/DD') + ' ~ ' + weekData[6].date.format('MM/DD') + ' ' + t('statistics.salesCalendar.weekSales')}
            </WeekTitle>
            <SalesCalendarWeekRow
              weekData={weekData}
              standardDate={today}
              selectedDate={selectedDate}
              salesDailyRef={salesDailyRef}
              onClickDateBox={onClickDateBox}
              onClickClose={() => setSelectedDate(null)}
              onClickSalesItem={onClickSalesItem} />
          </WeekSection>
        ))
      }
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  padding: 10px 0;

  box-sizing: border-box;
`;

const WeekSection = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  &:not(:first-child) {
    margin-top: 16px;
  }
`;

const WeekTitle = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.$color};

  margin: 0 0 8px 0;
`;
